import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import Group from 'components/utils/Group'
import NestableClickable from 'components/Common/NestableClickable'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import SearchTileRowInclusionItem from 'components/SearchKit/SearchTiles/shared/SearchTileRowInclusionItem'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import { formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { DrawerExperienceSearchTile } from './ExperienceDetailView'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import { isOfferBookedInItinerary } from 'tripPlanner/utils/chatItinerary'

const INCLUDED_ITEMS_TRUNCATE_AFTER = 2

const TileContainer = styled(NestableClickable)`
  display: grid;
  grid-template-columns: 1fr;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(300)} 1fr;
  }
`

const ImageSection = styled.div`
  position: relative;
  height: ${rem(200)};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    height: 100%;
    min-height: ${rem(250)};
  }
`

const AddToTripButtonContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  z-index: 1;
`

const ContentSection = styled(Group)`
  padding: ${rem(20)};
`

const TitleGroup = styled(Group)`
  flex: 1;
  min-width: 0;
`

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    margin-top: auto;
  }
`

const IncludedItemsGroup = styled(Group)`
  min-width: 0;
`

export interface ExperienceSearchTileProps {
  experience: DrawerExperienceSearchTile
  onSelectExperience: (experience: DrawerExperienceSearchTile) => void
  position?: number
  trackingOffer?: App.ExperienceOffer
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  onAfterAction?: () => void
}

interface ExperienceTileDisplayData {
  copy?: DrawerExperienceSearchTile['copy']
  id: string
  price?: number
  productType?: App.ExperienceOffer['productType']
}

function ExperienceSearchTile({
  experience,
  onSelectExperience,
  position,
  trackingOffer,
  itineraryData,
  onSendMessage,
  onAfterAction,
}: ExperienceSearchTileProps) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const geo = useContext(GeoContext)
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const hasFiredImpressionRef = useRef(false)
  const [isShowingAllIncludedItems, setIsShowingAllIncludedItems] = useState(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  const {
    name,
    location,
    images,
    rating,
    price,
    currencyCode,
  } = experience

  const displayData = useMemo<ExperienceTileDisplayData>(
    () => trackingOffer
      ? {
          copy: trackingOffer.copy,
          id: trackingOffer.id,
          price: trackingOffer.price ?? experience.price,
          productType: trackingOffer.productType,
        }
      : {
          copy: experience.copy,
          id: experience.offerId,
          price: experience.price,
          productType: experience.productType,
        },
    [experience.copy, experience.offerId, experience.price, experience.productType, trackingOffer],
  )

  useEffect(() => {
    if (!inView || !trackingOffer || position === undefined || hasFiredImpressionRef.current) {
      return
    }

    hasFiredImpressionRef.current = true

    dispatchOfferListEvent({
      type: OfferListEvents.productImpression,
      offer: trackingOffer,
      position,
    })
  }, [dispatchOfferListEvent, inView, position, trackingOffer])

  const handleSelectExperience = useCallback(() => {
    if (trackingOffer && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: trackingOffer,
        position,
      })
    }

    onSelectExperience(experience)
  }, [dispatchOfferListEvent, experience, onSelectExperience, position, trackingOffer])

  const locationLabel = useMemo(() => {
    const trackingOfferLocation = trackingOffer?.location
    return trackingOfferLocation?.city ?? trackingOfferLocation?.name ?? location
  }, [location, trackingOffer?.location])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest>>(() => [{
    offerId: displayData.id,
    price: displayData.price ?? 0,
    loyaltyProductType: 'experience',
  }], [displayData.id, displayData.price])

  const includedItems = useMemo<Array<App.OfferInclusion>>(() => {
    const mapIncludedItems = (items: Array<string>) => items.map((description, index) => ({
      id: `${displayData.id}-included-${index}`,
      description,
    }))

    if (displayData.productType === 'channel_manager') {
      return mapIncludedItems(displayData.copy?.highlights ?? [])
    }

    const parsedIncludedItems = (displayData.copy?.included ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => /^[-*]\s+/.test(line))
      .map((line) => line.replace(/^[-*]\s+/, '').trim())
      .filter(Boolean)
    if (parsedIncludedItems.length > 0) {
      return mapIncludedItems(parsedIncludedItems)
    }

    const fallbackContent = displayData.copy?.included || displayData.copy?.description
    if (!fallbackContent) {
      return []
    }

    return mapIncludedItems([fallbackContent
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/[*_`]/g, '')
      .trim()])
  }, [displayData.copy, displayData.id, displayData.productType])
  const visibleIncludedItems = useMemo(
    () => isShowingAllIncludedItems
      ? includedItems
      : includedItems.slice(0, INCLUDED_ITEMS_TRUNCATE_AFTER),
    [includedItems, isShowingAllIncludedItems],
  )
  const hiddenIncludedItemsCount = Math.max(0, includedItems.length - INCLUDED_ITEMS_TRUNCATE_AFTER)
  const handleShowMoreIncludedItems = useCallback(() => {
    setIsShowingAllIncludedItems(true)
  }, [])
  const handleShowLessIncludedItems = useCallback(() => {
    setIsShowingAllIncludedItems(false)
  }, [])

  const formattedPrice = useMemo(
    () => price !== undefined
      ? formatRoundedAmountWithDollarType(
          price,
          currencyCode ?? currentCurrency,
          geo.currentRegionCode,
        )
      : undefined,
    [price, currencyCode, currentCurrency, geo.currentRegionCode],
  )
  const isBooked = useMemo(() => {
    return isOfferBookedInItinerary(experience.offerId, itineraryData)
  }, [experience.offerId, itineraryData])

  const fromLabel = intl.formatMessage({
    id: 'dfcwDcH90Uz5YCe5kG37',
    defaultMessage: 'From',
    description: 'Price caption prefix when no night count is available on the map offer card',
  })

  const viewOfferLabel = intl.formatMessage({
    id: '0cKWSwyDZvKU0-9U6UrT',
    defaultMessage: 'View offer',
    description: 'Button to view hotel offer details on the hotel or room booking page',
  })

  return (
    <TileContainer ref={inViewRef} onClick={handleSelectExperience}>
      <ImageSection>
        {itineraryData && onSendMessage && (
          <AddToTripButtonContainer onClick={event => event.stopPropagation()}>
            <AddToTripAction
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              placeName={name}
              offerId={experience.offerId}
              offerType="experience"
              coordinates={experience.geoLocation}
              variant="filled"
              onAfterAction={onAfterAction}
            />
          </AddToTripButtonContainer>
        )}
        <ImageCarousel
          images={images}
          arrowBehaviour="interaction-visible"
          arrowStyle="ghost"
        />
      </ImageSection>

      <ContentSection direction="vertical" gap={12}>
        <Group
          direction="horizontal"
          gap={12}
          horizontalAlign="space-between"
          verticalAlign="start"
        >
          <TitleGroup direction="vertical" gap={4}>
            <BodyText variant="small" colour="light-foreground-alt">
              {locationLabel}
            </BodyText>
            <Heading variant="heading5" lineClamp={2}>
              {name}
            </Heading>
            {isBooked && <BookedLabel />}
          </TitleGroup>

          {rating !== undefined && rating > 0 && (
            <NumberRating
              rating={rating}
              onlyNumber
              variant="small"
            />
          )}
        </Group>

        <Group direction="vertical" gap={4}>
          <LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            hideStatusCredits
          />
        </Group>

        {visibleIncludedItems.length > 0 && (
          <Group direction="vertical" gap={4}>
            <IncludedItemsGroup direction="vertical" gap={4}>
              {visibleIncludedItems.map((item) => (
                <SearchTileRowInclusionItem key={item.id} inclusion={item} />
              ))}
            </IncludedItemsGroup>
            {!isShowingAllIncludedItems && hiddenIncludedItemsCount > 0 && (
              <TextLink size="medium" onClick={handleShowMoreIncludedItems}>
                {intl.formatMessage(
                  {
                    id: 'xwaI9U9USC4M_jSRxWZM',
                    defaultMessage: '+{count} more',
                    description: 'Link showing count of additional hidden experience inclusions in the chat trip planner drawer',
                  },
                  { count: hiddenIncludedItemsCount },
                )}
              </TextLink>
            )}
            {isShowingAllIncludedItems && hiddenIncludedItemsCount > 0 && (
              <TextLink size="medium" onClick={handleShowLessIncludedItems}>
                {intl.formatMessage({
                  id: 'C1gFMUHYFRNW_LrYNa1X',
                  defaultMessage: 'Show less',
                  description: 'Link to collapse expanded experience inclusions in the chat trip planner drawer',
                })}
              </TextLink>
            )}
          </Group>
        )}

        <PriceRow>
          {price !== undefined && (
            <Group direction="vertical" gap={2}>
              <BodyText variant="small" colour="light-foreground-alt">
                {fromLabel}
              </BodyText>
              <Heading variant="heading4">
                {formattedPrice}
              </Heading>
            </Group>
          )}
          <TextButton
            kind="primary"
            size="medium"
            onClick={handleSelectExperience}
          >
            {viewOfferLabel}
          </TextButton>
        </PriceRow>
      </ContentSection>
    </TileContainer>
  )
}

export default memo(ExperienceSearchTile)
